import { useMemo, useState } from 'react';
import { Disclosure } from '@/design-system/ui/Disclosure';
import { Input } from '@/design-system/ui/Field';
import { EmptyState } from '@/design-system/ui/States';
import { cn } from '@/design-system/ui/cn';
import { useSEO } from '@/core/seo/useSEO';
import { PageHeader } from '../components/PageHeader';
import { faqCategories } from '../data/faq';

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqCategories.flatMap((c) =>
    c.items.map((i) => ({ '@type': 'Question', name: i.q, acceptedAnswer: { '@type': 'Answer', text: i.a } })),
  ),
};

const chip = (active: boolean) =>
  cn('rounded-full border px-4 py-1.5 text-sm font-bold transition', active ? 'border-solid bg-solid text-white' : 'border-line bg-paper text-ink-2 hover:bg-fill');

export default function FaqPage() {
  useSEO({
    title: 'سوالات متداول',
    description: 'پاسخ به سوالات پرتکرار درباره خرید آنلاین بلیط تاکسی بین‌شهری، کنسلی و استرداد، سفر درب‌به‌درب، ارسال و رهگیری بار در بلینو.',
    canonical: '/faq',
    keywords: 'سوالات متداول بلینو, راهنمای خرید بلیط تاکسی بین شهری, استرداد بلیط بلینو, بیمه بار بلینو, رهگیری مرسوله',
    jsonLd: faqJsonLd,
  });

  const [query, setQuery] = useState('');
  const [active, setActive] = useState('all');

  const filtered = useMemo(() => {
    const q = query.trim();
    return faqCategories
      .filter((c) => active === 'all' || c.id === active)
      .map((c) => ({ ...c, items: c.items.filter((i) => !q || i.q.includes(q) || i.a.includes(q)) }))
      .filter((c) => c.items.length > 0);
  }, [query, active]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <PageHeader icon="help" title="سوالات متداول" subtitle="پاسخ سوالاتی که بیشتر کاربران درباره خرید بلیط، سفر درب‌به‌درب، رهگیری و ارسال بار می‌پرسند" />

      <Input type="search" aria-label="جستجو در سوالات متداول" placeholder="جستجو در سوالات متداول..." value={query} onChange={(e) => setQuery(e.target.value)} containerClassName="mb-4" />

      <div className="mb-6 flex flex-wrap gap-2" role="group" aria-label="دسته‌بندی">
        <button type="button" className={chip(active === 'all')} aria-pressed={active === 'all'} onClick={() => setActive('all')}>همه</button>
        {faqCategories.map((c) => (
          <button key={c.id} type="button" className={chip(active === c.id)} aria-pressed={active === c.id} onClick={() => setActive(c.id)}>
            {c.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="نتیجه‌ای برای جستجوی شما یافت نشد." />
      ) : (
        <div className="flex flex-col gap-7">
          {filtered.map((c) => (
            <section key={c.id} aria-labelledby={`faq-${c.id}`}>
              <h2 id={`faq-${c.id}`} className="mb-3 text-sm font-bold text-navy">{c.label}</h2>
              <div className="flex flex-col gap-3">
                {c.items.map((i) => (
                  <Disclosure key={i.q} summary={i.q}>
                    <p className="text-sm leading-loose text-ink-2">{i.a}</p>
                  </Disclosure>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
